import { useMemo } from "react";
import { ArrowRight, KeyRound, BookOpen, Activity } from "lucide-react";
import { Link } from "react-router-dom";

import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

interface QuickAction {
  label: string;
  description: string;
  icon: typeof KeyRound;
  to: string;
  enabled: boolean;
}

/**
 * Three calm shortcut cards. They ship disabled because the
 * destination pages arrive in a later release; the row stays so a
 * pilot walkthrough can preview product shape without surprising
 * the user with broken links.
 */
export function QuickActions() {
  const { t } = useTranslation();

  const actions = useMemo<ReadonlyArray<QuickAction>>(
    () => [
      {
        label: t("dashboard.quickActions.createKeyLabel"),
        description: t("dashboard.quickActions.createKeyDescription"),
        icon: KeyRound,
        to: "/api-keys/new",
        enabled: false,
      },
      {
        label: t("dashboard.quickActions.observabilityLabel"),
        description: t("dashboard.quickActions.observabilityDescription"),
        icon: Activity,
        to: "/observability",
        enabled: false,
      },
      {
        label: t("dashboard.quickActions.docsLabel"),
        description: t("dashboard.quickActions.docsDescription"),
        icon: BookOpen,
        to: "https://docs.monago.io",
        enabled: false,
      },
    ],
    [t]
  );

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {actions.map((action) => {
        const Icon = action.icon;
        const content = (
          <div
            className={cn(
              "group flex items-start justify-between gap-3 rounded-lg border border-ocean-100 bg-white p-4 transition-colors",
              action.enabled
                ? "cursor-pointer hover:border-ocean-200"
                : "cursor-not-allowed opacity-70"
            )}
          >
            <div className="flex min-w-0 items-start gap-3">
              <div className="rounded-md bg-ocean-50 p-2 text-ocean-600">
                <Icon size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-ocean-800">
                  {action.label}
                </div>
                <div className="mt-0.5 text-xs text-neutral-500">
                  {action.description}
                </div>
              </div>
            </div>
            <ArrowRight
              size={16}
              className={cn(
                "shrink-0 text-ocean-400 transition-transform",
                action.enabled && "group-hover:translate-x-0.5"
              )}
            />
          </div>
        );
        if (!action.enabled) {
          return (
            <div
              key={action.label}
              title={t("dashboard.quickActions.comingSoon")}
            >
              {content}
            </div>
          );
        }
        return (
          <Link key={action.label} to={action.to}>
            {content}
          </Link>
        );
      })}
    </div>
  );
}
